/**
 * Tab definitions for the marketing configurator, kept dependency-free on
 * purpose. VisualBuilderSection and PricingSection import these statically
 * while loading VisualBuilderDemo through `next/dynamic({ ssr: false })`.
 * Exporting them from VisualBuilderDemo itself put the demo, three.js, the
 * viewer and StaticBuilderPreview into the page's eager bundle, so the
 * "ssr:false" previews were already resolved at hydration and rendered where
 * the server had rendered the loading placeholder — a hydration mismatch.
 *
 * `layoutType` values are the engine's LAYOUT_TYPES strings, written out so
 * this file never pulls @timbercloud/3d-engine into the eager bundle either.
 */

/**
 * Layout types for demo. The keys are shared with StaticBuilderPreview, so they
 * stay product words rather than engine layout types.
 */
export const DEMO_LAYOUT_TYPES = {
  DOOR: 'door',
  FACE_FRAME: 'face_frame',
  CABINET: 'cabinet',
  DRAWER: 'drawer',
  BOOKSHELF: 'bookshelf',
};

/**
 * Each tab is a product template as a new company gets it: its layout type,
 * with the template's field mappings resolved against the first option of each
 * attribute (Stile & Rail Width 2-1/4", Flat Panel, 3/4" Plywood, Face Frame…).
 * The one deliberate pick is the bookshelf's "3 Shelves" — an empty case reads
 * as a broken render, not a bookshelf.
 */
export const DEMO_CONFIGS = {
  [DEMO_LAYOUT_TYPES.DOOR]: {
    label: 'Shaker Door',
    layoutType: 'framed',
    width: 18,
    height: 24,
    fields: {
      leftStile: 2.25,
      rightStile: 2.25,
      topRail: 2.25,
      bottomRail: 2.25,
      panelStyle: 'flat',
      panelThickness: 0.25,
      frameDepth: 0.75,
    },
    is2D: true,
  },
  [DEMO_LAYOUT_TYPES.FACE_FRAME]: {
    label: 'Face Frame',
    layoutType: 'face_frame',
    width: 30,
    height: 36,
    fields: {
      leftStile: 2.25,
      rightStile: 2.25,
      topRail: 2.25,
      bottomRail: 2.25,
      frameDepth: 0.75,
    },
    is2D: true,
  },
  [DEMO_LAYOUT_TYPES.CABINET]: {
    label: 'Base Cabinet',
    layoutType: 'base_cabinet',
    width: 24,
    height: 34.5,
    depth: 24,
    fields: {
      materialThickness: 0.75,
      backThickness: 0.25,
      shelfCount: 0,
      shelfThickness: 0.75,
      faceFrameEnabled: 1,
      faceFrameStileWidth: 1.5,
    },
  },
  [DEMO_LAYOUT_TYPES.DRAWER]: {
    label: 'Drawer Box',
    layoutType: 'drawer_box',
    width: 18,
    height: 6,
    depth: 20,
    fields: {
      materialThickness: 0.75,
      bottomThickness: 0.25,
    },
  },
  [DEMO_LAYOUT_TYPES.BOOKSHELF]: {
    label: 'Bookshelf',
    layoutType: 'bookshelf',
    width: 36,
    height: 48,
    depth: 12,
    fields: {
      materialThickness: 0.75,
      backThickness: 0.25,
      shelfCount: 3,
    },
  },
};
